import React, { useMemo, useContext, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';

import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-campaign-react/mca-instrumentation';
import { useESCGlobalizationPilotValue } from '@bingads-webui-campaign-react/mca-core';
import { useAccount, useCurrencies } from '@bingads-webui/overreact-cc';
import { useAccountLevelVariables, useCustomerLevelVariables } from '@bingads-webui-campaign-react/up-data';
import { usePostponeReadVariables } from '@bingads-webui/overreact-bingads';

import { useContextData } from './use-context-data';

export const Context = React.createContext();
export const useLocaleAndMarketContext = () => useContext(Context);

export const LocaleAndMarketContext = (props) => {
  const { children } = props;
  const serverContext = useContext(ServerContext);
  const {
    CurrentUser,
    UserRegion,
    CurrentAccount,
    CurrentLocale,
  } = serverContext;

  const config =
  {
    fetch: {
      postponeRead: false,
    },
  };

  const [{ data: account }, { read: readAccount }] = useAccount(useAccountLevelVariables(), usePostponeReadVariables());
  const [{ data: currencies }] = useCurrencies(useCustomerLevelVariables(), config);

  useEffect(() => {
    if (!_.isEmpty(CurrentAccount) && !account) {
      readAccount();
    }
  }, [CurrentAccount, account, readAccount]);

  const { Market, Language } = CurrentUser || {};
  const [instrumentationService] = useContext(InstrumentationContext);
  const instrumentationActivity = useMemo(() => {
    if (instrumentationService && instrumentationService.instrumentation) {
      const instrumentationScenario = instrumentationService.instrumentation.localeAndMarketContext.create();
      return instrumentationScenario.localeAndMarketContext.create();
    }
    return { trace: _.noop, error: _.noop };
  }, [instrumentationService]);

  const displayLanguage = CurrentLocale || document.documentElement.lang;
  instrumentationActivity.trace(`LocaleAndMarketContext displayLanguage: ${displayLanguage}`);

  const isGlobalizationEnabled = useESCGlobalizationPilotValue();
  const escgConfig = useMemo(() => {
    if (!isGlobalizationEnabled) {
      return {
        languages: [{
          isEnabled: true, isFbChannelAllowed: true, isGoogleChannelAllowed: true, language: 'English',
        }],
        markets: [{
          isEnabled: true, isFbChannelAllowed: true, isGoogleChannelAllowed: true, countryCode: 'US',
        }],
        targetLocations: [{
          isEnabled: true, isFbChannelAllowed: true, isGoogleChannelAllowed: true, countryCode: 'US',
        }],
      };
    }
    return serverContext.EscgConfig;
  }, [isGlobalizationEnabled, serverContext.EscgConfig]);

  const value = useContextData({
    instrumentationActivity,
    countryFromServerContext: _.get(account, ['BusinessAddress', 'Country']) || UserRegion || Market,
    Language,
    currencies,
    escgConfig,
  });

  if (_.isEmpty(value)) {
    return null;
  }
  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

LocaleAndMarketContext.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
